import { Pressable, StyleSheet } from 'react-native'

import { useHapticFeedback } from '../haptics/HapticsContext'
import { FigmaChevronLeftIcon } from '../icons/FigmaSystemIcons'
import { Box, Text, useTheme } from '../theme/restyle'
import type { NumpadKey } from '../utils/numpadInput'

// Shared with the delete glyph, which sizes to the digits.
const KEY_FONT_SIZE = 24

// The design sets the outer columns against the keypad's edges. The touch
// target is still the whole share.
const COLUMN_ALIGNMENT = ['flex-start', 'center', 'flex-end'] as const

const ROWS: NumpadKey[][] = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['.', '0', 'backspace'],
]

export interface NumPadProps {
  onPressKey: (key: NumpadKey) => void
  /** The delete key carries a glyph, so its label can't come from the key. */
  backspaceAccessibilityLabel: string
}

/**
 * The withdrawal amount keypad. Both dimensions come from the container, so
 * the keys grow with the device rather than leaving a void above them.
 *
 * Emits keys rather than a value: `applyNumpadKey` owns the grammar.
 */
export function NumPad({
  onPressKey,
  backspaceAccessibilityLabel,
}: NumPadProps) {
  const theme = useTheme()
  const haptic = useHapticFeedback()

  return (
    <Box flex={1}>
      {ROWS.map((row) => (
        <Box key={row.join('')} flexDirection="row" flex={1}>
          {row.map((key, column) => (
            <Pressable
              key={key}
              onPress={() => {
                // Before the handler: the tap should land with the finger, not
                // after whatever the amount change costs.
                haptic()
                onPressKey(key)
              }}
              accessibilityRole="button"
              accessibilityLabel={
                key === 'backspace' ? backspaceAccessibilityLabel : key
              }
              style={[styles.key, { alignItems: COLUMN_ALIGNMENT[column] }]}
            >
              {key === 'backspace' ? (
                <FigmaChevronLeftIcon
                  size={KEY_FONT_SIZE}
                  color={theme.content.elements.primary}
                />
              ) : (
                <Text
                  weight="medium"
                  style={[
                    styles.label,
                    key === '.' ? styles.decimalLabel : null,
                  ]}
                >
                  {key}
                </Text>
              )}
            </Pressable>
          ))}
        </Box>
      ))}
    </Box>
  )
}

const styles = StyleSheet.create({
  key: { flex: 1, justifyContent: 'center' },
  label: {
    fontSize: KEY_FONT_SIZE,
    minWidth: KEY_FONT_SIZE,
    lineHeight: 32,
    letterSpacing: -0.36,
    textAlign: 'center',
  },
  decimalLabel: {
    fontSize: 48,
    lineHeight: 56,
    transform: [{ translateY: -14 }],
  },
})
